import React, { useState } from "react";
import { Box, Typography, Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGetFormSubmissions } from "../api/reactQueryHook/useFormAssignments";
import MagicLoader from "./MagicLoader";

interface FormSubmissionsDialogProps {
  open: boolean;
  fileName: string;
  // The file's connected-form id; undefined disables the query (e.g. before open).
  connectionId?: number;
  onClose: () => void;
}

const formatSubmittedAt = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

// Derive a human filename from the presigned URL (strip query string + decode the last path segment).
const fileNameFromUrl = (url: string): string => {
  try {
    const path = url.split("?")[0];
    return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1)) || "Submission";
  } catch {
    return "Submission";
  }
};

const FormSubmissionsDialog: React.FC<FormSubmissionsDialogProps> = ({ open, fileName, connectionId, onClose }) => {
  // Only fetch while the dialog is open so presigned URLs are fresh (~30 min TTL).
  const { data, isFetching } = useGetFormSubmissions(open ? connectionId : undefined);
  const submissions = data?.data ?? [];

  // The submission currently previewed in the embedded viewer (null = closed).
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      // Fixed dialog size — does not grow/shrink with the number of submissions; the card area scrolls.
      // backgroundImage: none strips MUI's dark-mode elevation overlay so background.paper renders exactly.
      PaperProps={{ sx: { width: 1040, height: 640, maxWidth: "none", m: 2, bgcolor: "background.paper", backgroundImage: "none", border: "1px solid", borderColor: "divider", borderRadius: "10px", display: "flex", flexDirection: "column", position: "relative" } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "text.primary" }}>
          Form Submissions for {fileName}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Magic loading centered over the whole dialog (which is itself centered on the page). */}
      <MagicLoader isLoading={isFetching} variant="inline" />

      <DialogContent sx={{ p: 3, flex: 1, minHeight: 0, overflowY: "auto" }}>
        {isFetching ? null : submissions.length === 0 ? (
          <Typography sx={{ color: "text.secondary", textAlign: "center", py: 6, fontSize: "0.9rem" }}>
            No submissions yet.
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, 320px)", gap: 2, justifyContent: "flex-start" }}>
            {submissions.map(s => {
              // submission_url is "" when no file is stored — render a disabled label instead of a dead link.
              const hasFile = !!s.submission_url;
              return (
                <Box
                  key={s.submission_id}
                  // Whole card opens the submitted form in the embedded preview; no-op when there's no stored file.
                  onClick={() => { if (hasFile) setPreview({ url: s.submission_url, name: fileNameFromUrl(s.submission_url) }); }}
                  // Fixed card size — width/height stay constant regardless of content length.
                  sx={{
                    width: 320, height: 150, bgcolor: "background.default",
                    border: "1px solid", borderColor: "divider", borderRadius: "10px",
                    p: 2.5, display: "flex", flexDirection: "column", gap: 1, overflow: "hidden",
                    cursor: hasFile ? "pointer" : "default",
                    // Elevation effect on hover; lifts from background.default to background.paper.
                    transition: "background-color 0.12s, box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": hasFile ? { bgcolor: "background.paper", boxShadow: 8 } : undefined,
                  }}
                >
                  <Typography
                    noWrap
                    title={s.email_id}
                    sx={{ fontWeight: 700, fontSize: "0.95rem", color: hasFile ? "primary.main" : "text.disabled" }}
                  >
                    {hasFile ? s.email_id : `${s.email_id} (no file)`}
                  </Typography>
                  <Typography noWrap title={s.file_name} sx={{ fontSize: "0.85rem", color: "text.primary" }}>
                    <Box component="span" sx={{ fontWeight: 700 }}>File:</Box> {s.file_name}
                  </Typography>
                  <Typography noWrap title={s.company_name} sx={{ fontSize: "0.85rem", color: "text.primary" }}>
                    <Box component="span" sx={{ fontWeight: 700 }}>Company:</Box> {s.company_name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                    Submitted: {formatSubmittedAt(s.submitted_at)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>

    {/* Embedded preview — renders the submitted file inline (browser-native PDF viewer) instead of a new tab. */}
    <Dialog
      open={!!preview}
      onClose={() => setPreview(null)}
      PaperProps={{ sx: { width: "90vw", height: "90vh", maxWidth: "none", m: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: "10px", display: "flex", flexDirection: "column" } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2, pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography noWrap title={preview?.name} sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary" }}>
          {preview?.name} Preview
        </Typography>
        <IconButton size="small" onClick={() => setPreview(null)} sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {preview && (
          <Box
            component="iframe"
            src={preview.url}
            title={preview.name}
            sx={{ width: "100%", height: "100%", border: "none", display: "block", bgcolor: "#fff" }}
          />
        )}
      </Box>
    </Dialog>
    </>
  );
};

export default FormSubmissionsDialog;
